import { Label } from '@teach/ui'
import type { ContentLang } from '../../editorSlice'
import type { CourseWithoutBlocks, CourseUpdate } from '@/types/course'
import { BilingualField } from '@/components/common/BilingualField'
import { ImageUpload } from '../shared/ImageUpload'
import { ImagePromptField } from '../shared/ImagePromptField'
import { DisabledHint } from '../EditorHeader/translation/DisabledHint'
import { FIELD_LIMITS } from '../../constants/fieldLimits'
import { useSaveAfterPause } from '../../hooks/useSaveAfterPause'
import { clearable } from '../../lib/coursePatch'
import { REVIEW_LOCK_TEXT } from '../../lib/reviewLockText'
import { untitledLabel } from '@/lib/untitled'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  /** Язык полей названия и описания — местный язык настроек, не язык правки редактора. */
  lang?: ContentLang
  local: CourseWithoutBlocks
  setLocal: (next: CourseWithoutBlocks) => void
  save: (patch: CourseUpdate) => Promise<boolean>
  isCourseAuthor: boolean
  /**
   * Идёт прогон перевода (§7.5). Название и описание курса он переписывает
   * так же, как названия уроков (`LessonInfoPanel`): снимок текста взят на
   * старте, и правка по ходу молча исчезнет при записи патча. Гейт — тот же
   * `useIsCourseTextLocked`, что у полей урока.
   */
  isTextLocked: boolean
  /** Курс на проверке (`useIsCourseFrozen`): подпись полей называет проверку,
   *  а картинки курса (баннер, промпт, стиль, образцы) гаснут — они уходят в
   *  ту же заявку. Перевод их не трогает, поэтому это отдельный флаг. */
  isFrozen: boolean
}

export const BasicInfoSection = ({
  lang,
  local,
  setLocal,
  save,
  isCourseAuthor,
  isTextLocked,
  isFrozen,
}: Props) => {
  const m = useMessages(messages)
  const textDisabled = !isCourseAuthor || isTextLocked
  const disabledTitle = !isCourseAuthor ? m.noRights : undefined
  const assetDisabled = !isCourseAuthor || isFrozen
  const assetTitle = isFrozen && isCourseAuthor ? REVIEW_LOCK_TEXT : disabledTitle

  const saveName = () => save({ name: local.name, nameEn: local.nameEn })
  const saveDescription = () =>
    save({ description: local.description, descriptionEn: local.descriptionEn })
  // Пустое название — промежуточное состояние набора, оно уходит только при выходе из поля.
  useSaveAfterPause(
    `${local.name}\n${local.nameEn}`,
    saveName,
    !textDisabled && Boolean(local.name?.trim() || local.nameEn?.trim()),
  )
  useSaveAfterPause(`${local.description}\n${local.descriptionEn}`, saveDescription, !textDisabled)

  const lockHint = isTextLocked ? (isFrozen ? REVIEW_LOCK_TEXT : m.translationLockNote) : null

  return (
    <div className="space-y-5 [&_label]:text-lg [&_label]:font-medium">
      <DisabledHint text={lockHint}>
        <div className="space-y-5">
          <BilingualField
            value={{ ru: local.name, en: local.nameEn }}
            onChange={(v) => setLocal({ ...local, name: v.ru, nameEn: v.en })}
            onBlur={saveName}
            lang={lang}
            label={m.courseName}
            // Пустое название — серой подсказкой, а не подставленным текстом.
            // Подсказка на языке поля: на EN видно, что правится английское название.
            placeholder={untitledLabel(lang)}
            maxLength={FIELD_LIMITS.COURSE_NAME}
            disabled={textDisabled}
            title={isTextLocked ? undefined : disabledTitle}
          />
          <BilingualField
            value={{ ru: local.description, en: local.descriptionEn }}
            onChange={(v) => setLocal({ ...local, description: v.ru, descriptionEn: v.en })}
            onBlur={saveDescription}
            lang={lang}
            label={m.description}
            as="textarea"
            maxLength={FIELD_LIMITS.COURSE_DESCRIPTION}
            disabled={textDisabled}
            title={isTextLocked ? undefined : disabledTitle}
          />
        </div>
      </DisabledHint>
      <div className="space-y-3">
        <Label>{m.courseBanner}</Label>
        {/* Баннер 16:9 слева, промпт справа — на узком экране друг под другом. */}
        <div className="grid items-start gap-4 md:grid-cols-2">
          <ImageUpload
            widescreen
            value={local.banner}
            onChange={(url) => {
              setLocal({ ...local, banner: url })
              // null бэк игнорирует — чтобы снять баннер, шлём пустую строку. Промис
              // возвращаем: ImageUpload удалит старый блоб только после успешного PUT.
              return save({ banner: clearable(url) })
            }}
            disabled={assetDisabled}
          />
          {/* Второго слота у bannerPrompt нет (§5.2) — язык контента тут ни при чём. */}
          <ImagePromptField
            value={local.bannerPrompt ?? ''}
            hasImage={Boolean(local.banner)}
            hasSecondSlot={false}
            disabled={assetDisabled}
            onChange={(next) => setLocal({ ...local, bannerPrompt: next })}
            // Значение берём из аргумента, а НЕ из `local`: `setLocal` этого же
            // события ещё не применился, и сохранился бы предыдущий текст.
            onCommit={(next) => void save({ bannerPrompt: next })}
          />
        </div>
      </div>
    </div>
  )
}
